import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  checkAbility,
  SettingsPanelProvider,
  SidebarItemsModel,
  SidebarItemsModelV,
} from '@jaam/web-core';
import PAYROLL_ITEMS from '../constants/payrollSidebarItems';
import { useRecoilValue } from 'recoil';
import { authState } from '../state/authState';
import { ViewportProvider } from './navigation/ViewportContext';
import { OnlineStatusProvider } from './navigation/OnlineStatusContext';

interface NavigationContextType {
  showBackButton: boolean;
  setShowBackButton: (show: boolean) => void;
  backRoute: string | null;
  setBackRoute: (route: string | null) => void;
  navBarItems: Array<SidebarItemsModel | SidebarItemsModelV>;
  setNavBarItems: (items: Array<SidebarItemsModel | SidebarItemsModelV>) => void;
  showSideMenu: boolean;
  setShowSideMenu: (show: boolean) => void;
  verifyAbility: ReturnType<typeof checkAbility>;
}

const NavigationContext = createContext<NavigationContextType | null>(null);

const NavigationProviderInner: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [showBackButton, setShowBackButton] = useState(false);
  const [backRoute, setBackRoute] = useState<string | null>(null);
  const [navBarItems, setNavBarItems] = useState<
    Array<SidebarItemsModel | SidebarItemsModelV>
  >([...PAYROLL_ITEMS] as SidebarItemsModel[]);
  const [showSideMenu, setShowSideMenu] = useState(false);
  const [prevRoute, setPrevRoute] = useState('/');
  const { user, parsedPermissions } = useRecoilValue(authState);

  const verifyAbility = useMemo(
    () => checkAbility(parsedPermissions || [], user?.id || 0),
    [parsedPermissions, user?.id],
  );

  const location = useLocation();
  useEffect(() => {
    if (location?.pathname.includes('company')) {
      setBackRoute(prevRoute);
    } else {
      setPrevRoute(location.pathname);
    }
  }, [location, prevRoute]);

  const contextValue = useMemo(
    () => ({
      showBackButton,
      setShowBackButton,
      backRoute,
      setBackRoute,
      navBarItems,
      setNavBarItems,
      showSideMenu,
      setShowSideMenu,
      verifyAbility,
    }),
    [
      showBackButton,
      backRoute,
      navBarItems,
      showSideMenu,
      verifyAbility,
    ],
  );

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <ViewportProvider>
      <OnlineStatusProvider>
        <SettingsPanelProvider>
          <NavigationProviderInner>{children}</NavigationProviderInner>
        </SettingsPanelProvider>
      </OnlineStatusProvider>
    </ViewportProvider>
  );
};

export const useLocalNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error(
      'useLocalNavigation must be used within a NavigationProvider',
    );
  }
  return context;
};
